import type { FormEvent, ReactElement } from 'react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { ChannelDetail, ChannelVisibility } from '@contracts/channels';
import { ApiError } from '../api/client';
import { useChannel, useChannelActions, useMe, useOrg, useSeriesList } from '../api/queries';
import {
  BackLink,
  Broken,
  Button,
  Card,
  Confirm,
  Empty,
  Field,
  Icon,
  ListSwitch,
  MenuButton,
  Modal,
  Pill,
  Rows,
  SkeletonRows,
  TextArea,
  Who,
  useNotices,
} from '../ui';
import { ago, dateLabel } from '../lib/time';

/**
 * One channel (PRD 58): what was posted to it, newest first; the series it
 * follows; the people in it.
 *
 * ⚠ Somebody who has not joined a public channel sees who is in it and what it
 * is for — never its meetings. Joining is what shares them.
 */
export function ChannelPage(): ReactElement {
  const { id = '' } = useParams();
  const channel = useChannel(id);
  if (channel.isPending) {
    return (
      <div className="page">
        <SkeletonRows rows={5} />
      </div>
    );
  }
  if (channel.isError) {
    const gone = channel.error instanceof ApiError && channel.error.status === 404;
    return (
      <div className="page">
        <Card flush>
          {gone ? (
            <Empty
              tone="bad"
              lead="No such collection — or not one you can open."
              action={
                <Link to="/channels" className="btn">
                  Collections
                </Link>
              }
            >
              A private collection is visible only to the people in it.
            </Empty>
          ) : (
            <Broken what="This channel" retry={() => void channel.refetch()} />
          )}
        </Card>
      </div>
    );
  }
  return <Loaded c={channel.data} />;
}

function Loaded({ c }: { c: ChannelDetail }): ReactElement {
  const actions = useChannelActions();
  const me = useMe();
  const navigate = useNavigate();
  const { show } = useNotices();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const member = c.myRole !== null;
  const fail = (err: Error) => show({ tone: 'failure', text: err.message });

  const join = () =>
    actions.join.mutate(c.id, {
      onSuccess: () => show({ tone: 'success', text: `You are in #${c.name}.` }),
      onError: fail,
    });
  const leave = () =>
    actions.leave.mutate(c.id, {
      onSuccess: () => {
        show({ tone: 'success', text: `You left ${c.name}.` });
        if (c.visibility === 'PRIVATE') void navigate('/channels');
      },
      onError: fail,
    });

  return (
    <div className="page stack-lg">
      <ListSwitch on="channels" />
      <div className="stack" style={{ gap: 6 }}>
        <div className="toolbar">
          <BackLink to="/channels">Collections</BackLink>
        </div>
        <div className="page-head" style={{ alignItems: 'flex-start', marginBottom: 0 }}>
          <div className="stack" style={{ gap: 4 }}>
            <h1 className="t-title">{c.name}</h1>
            <p className="mono">
              {c.visibility === 'PUBLIC' ? 'public' : 'private'} · {c.memberCount}{' '}
              {c.memberCount === 1 ? 'person' : 'people'} · {c.meetingCount}{' '}
              {c.meetingCount === 1 ? 'meeting' : 'meetings'}
              {c.lastPostedAt ? ` · last added ${ago(c.lastPostedAt)}` : ''}
            </p>
            {c.description ? <p className="t-body topic-what-lead">{c.description}</p> : null}
          </div>
          <div className="cluster" style={{ gap: 8 }}>
            {c.canJoin ? (
              <Button
                variant="primary"
                onClick={join}
                working={actions.join.isPending ? 'Joining…' : false}
              >
                Join
              </Button>
            ) : null}
            {member ? (
              <MenuButton
                label="···"
                variant="quiet"
                items={[
                  ...(c.canManage
                    ? [{ label: 'Edit the collection…', onSelect: () => setEditing(true) }]
                    : []),
                  { label: 'Leave the collection', onSelect: leave },
                  ...(c.canManage
                    ? [
                        {
                          label: 'Delete the collection…',
                          danger: true,
                          onSelect: () => setDeleting(true),
                        },
                      ]
                    : []),
                ]}
              />
            ) : null}
          </div>
        </div>
      </div>

      <div className="grid-2 aside-fluid">
        <div className="stack-lg">
          <Card
            title="Meetings"
            aside={member ? <span className="mono">newest first</span> : null}
            flush
          >
            {!member ? (
              <Empty lead={`Join to see its ${c.meetingCount} meetings.`}>
                Joining shares with you every meeting in it — and every one added later.
              </Empty>
            ) : c.meetings.length === 0 ? (
              <Empty lead="No meetings in it yet.">
                Add a meeting from its own page, or have a series add every meeting here (on the
                right).
              </Empty>
            ) : (
              <Rows>
                {c.meetings.map((m) => (
                  <div key={m.id} className="row">
                    <Link to={`/m/${m.id}`} className="row-main grow" style={{ color: 'inherit' }}>
                      <span className="row-title">{m.title}</span>
                      {m.lead ? <span className="row-sub">{m.lead}</span> : null}
                      <span className="row-sub mono">
                        {dateLabel(m.startedAt)}
                        {m.postedBy ? ` · added by ${m.postedBy}` : ''}
                        {m.commitmentCount ? ` · ${m.commitmentCount} actions` : ''}
                      </span>
                    </Link>
                    {m.canRemove ? (
                      <Button
                        size="sm"
                        variant="quiet"
                        aria-label={`Take ${m.title} out of ${c.name}`}
                        onClick={() =>
                          actions.unpost.mutate(
                            { id: c.id, meetingId: m.id },
                            {
                              onSuccess: () => show({ tone: 'success', text: `Out of ${c.name}.` }),
                              onError: fail,
                            },
                          )
                        }
                      >
                        <Icon name="close" size={13} />
                      </Button>
                    ) : null}
                  </div>
                ))}
              </Rows>
            )}
          </Card>
        </div>

        <div className="stack-lg">
          <SeriesCard c={c} />
          <PeopleCard c={c} myId={me.data?.id ?? ''} />
        </div>
      </div>

      {editing ? <EditChannel c={c} onClose={() => setEditing(false)} /> : null}
      {deleting ? (
        <Confirm
          title={`Delete ${c.name}?`}
          noun="Delete it"
          working={actions.remove.isPending}
          onClose={() => setDeleting(false)}
          onConfirm={() =>
            actions.remove.mutate(c.id, {
              onSuccess: () => {
                show({ tone: 'success', text: `${c.name} is gone. Its meetings are not.` });
                void navigate('/channels');
              },
              onError: (err) => {
                setDeleting(false);
                fail(err);
              },
            })
          }
        >
          The meetings in it stay where they are — only this collection, and what it shared, go.
          People who could open a meeting only through it will not any more.
        </Confirm>
      ) : null}
    </div>
  );
}

/** The series whose every meeting is posted here, as each happens. */
function SeriesCard({ c }: { c: ChannelDetail }): ReactElement {
  const actions = useChannelActions();
  const series = useSeriesList();
  const { show } = useNotices();
  const member = c.myRole !== null;
  const followed = new Set(c.series.map((s) => s.seriesKey));
  const offer = (series.data ?? []).filter((s) => !followed.has(s.seriesKey));
  return (
    <Card title="Series it follows" flush>
      <Rows>
        {c.series.map((s) => (
          <div key={s.seriesKey} className="row dense">
            <Link
              to={`/series/${encodeURIComponent(s.seriesKey)}`}
              className="grow truncate"
              style={{ color: 'inherit' }}
            >
              {s.title}
            </Link>
            {s.addedBy ? <span className="mono">{s.addedBy}</span> : null}
            {member ? (
              <Button
                size="sm"
                variant="quiet"
                aria-label={`Stop following ${s.title}`}
                onClick={() =>
                  actions.unfollow.mutate(
                    { id: c.id, seriesKey: s.seriesKey },
                    {
                      onSuccess: () =>
                        show({
                          tone: 'success',
                          text: `New meetings of ${s.title} will not be added here. What was added stays.`,
                        }),
                      onError: (err) => show({ tone: 'failure', text: err.message }),
                    },
                  )
                }
              >
                <Icon name="close" size={13} />
              </Button>
            ) : null}
          </div>
        ))}
        {c.series.length === 0 ? (
          <div className="row dense">
            <span className="t-secondary">
              None yet — a series adds every meeting here as it happens.
            </span>
          </div>
        ) : null}
      </Rows>
      {member && offer.length > 0 ? (
        <div className="row dense">
          <MenuButton
            label="+ Follow a series"
            size="sm"
            variant="quiet"
            align="left"
            items={offer.map((s) => ({
              id: s.seriesKey,
              label: s.title,
              onSelect: () =>
                actions.follow.mutate(
                  { id: c.id, seriesKey: s.seriesKey },
                  {
                    onSuccess: ({ posted }) =>
                      show({
                        tone: 'success',
                        text: `${s.title} adds its meetings here now${posted ? ` — ${posted} meeting${posted === 1 ? '' : 's'} added already` : ''}. Only meetings you may share are added.`,
                      }),
                    onError: (err) => show({ tone: 'failure', text: err.message }),
                  },
                ),
            }))}
          />
        </div>
      ) : null}
    </Card>
  );
}

function PeopleCard({ c, myId }: { c: ChannelDetail; myId: string }): ReactElement {
  const actions = useChannelActions();
  const org = useOrg();
  const { show } = useNotices();
  const member = c.myRole !== null;
  // ⚠ Chosen from the workspace, not typed: only its members can be added, so
  // a blank address field asked people to remember what the app already knew
  // (Vladimir, 2026-09-24: "add people to the channel does not suggest users
  // from organization, it should").
  const inIt = new Set(c.members.map((m) => m.userId));
  const candidates = (org.data?.members ?? [])
    .filter((m) => !inIt.has(m.userId))
    .sort((a, b) => (a.name ?? a.email).localeCompare(b.name ?? b.email));
  const add = (email: string, name: string) => {
    actions.addMember.mutate(
      { id: c.id, email },
      {
        onSuccess: () =>
          show({ tone: 'success', text: `${name} is in — they see every meeting here now.` }),
        onError: () =>
          show({
            tone: 'failure',
            text: 'Nobody in your workspace has that address — they need a Ptitim account first.',
          }),
      },
    );
  };
  return (
    <Card title="People" aside={<span className="mono">{c.memberCount}</span>} flush>
      <Rows>
        {c.members.map((m) => (
          <div key={m.userId} className="row dense">
            <span className="grow">
              <Who email={m.email} name={m.name ?? m.email} />
            </span>
            {m.role === 'OWNER' ? <Pill tone="green">owner</Pill> : null}
            {c.canManage && m.userId !== myId ? (
              <MenuButton
                label="···"
                size="sm"
                variant="quiet"
                items={[
                  ...(m.role !== 'OWNER'
                    ? [
                        {
                          label: 'Make an owner',
                          onSelect: () => actions.makeOwner.mutate({ id: c.id, userId: m.userId }),
                        },
                      ]
                    : []),
                  {
                    label: 'Remove from the collection',
                    danger: true,
                    onSelect: () =>
                      actions.removeMember.mutate(
                        { id: c.id, userId: m.userId },
                        { onError: (err) => show({ tone: 'failure', text: err.message }) },
                      ),
                  },
                ]}
              />
            ) : null}
          </div>
        ))}
      </Rows>
      {member ? (
        <div className="row dense cluster" style={{ gap: 8 }}>
          {candidates.length ? (
            <MenuButton
              label={actions.addMember.isPending ? 'Adding…' : '+ Add someone'}
              search
              size="sm"
              items={candidates.map((m) => ({
                id: m.userId,
                label: m.name ?? m.email,
                sub: m.name ? m.email : undefined,
                onSelect: () => add(m.email, m.name ?? m.email),
              }))}
            />
          ) : (
            <span className="why">
              {org.isPending
                ? 'reading your workspace…'
                : `everyone in ${org.data?.name ?? 'your workspace'} is here`}
            </span>
          )}
        </div>
      ) : null}
    </Card>
  );
}

function EditChannel({ c, onClose }: { c: ChannelDetail; onClose: () => void }): ReactElement {
  const actions = useChannelActions();
  const { show } = useNotices();
  const [name, setName] = useState(c.name);
  const [description, setDescription] = useState(c.description ?? '');
  const [visibility, setVisibility] = useState<ChannelVisibility>(c.visibility);
  const save = (e: FormEvent) => {
    e.preventDefault();
    actions.update.mutate(
      {
        id: c.id,
        body: { name: name.trim(), description: description.trim() || null, visibility },
      },
      {
        onSuccess: () => {
          onClose();
          show({ tone: 'success', text: 'Saved.' });
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  };
  return (
    <Modal
      title={`Edit ${c.name}`}
      onClose={onClose}
      foot={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            form="edit-channel"
            disabled={!name.trim()}
            working={actions.update.isPending ? 'Saving…' : false}
          >
            Save
          </Button>
        </>
      }
    >
      <form id="edit-channel" className="stack" style={{ gap: 12 }} onSubmit={save}>
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
        <TextArea
          label="What it is for"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          maxLength={300}
        />
        <div className="cluster" style={{ gap: 6 }}>
          <Button
            size="sm"
            variant={visibility === 'PUBLIC' ? 'primary' : 'quiet'}
            onClick={() => setVisibility('PUBLIC')}
          >
            Anybody in the workspace
          </Button>
          <Button
            size="sm"
            variant={visibility === 'PRIVATE' ? 'primary' : 'quiet'}
            onClick={() => setVisibility('PRIVATE')}
          >
            Only people in it
          </Button>
        </div>
      </form>
    </Modal>
  );
}
